import { useEditorStore } from '@/stores/editor/editor-store';
import { templateInstantiationService } from '@/services/ai/TemplateInstantiationService';
import { Component, ComponentType } from '@/types';
import type { CanvasSettings } from '@/types';
import type { ComponentData } from '@/components/ai/types';
import type { AnyComponentProperties } from '@/types/component-properties';

/**
 * Module-level helper: adjust a CSS hex color by a brightness percent.
 * Private — only used by parseCanvasChanges.
 */
function adjustColorBrightness(color: string, percent: number): string {
  try {
    if (!color || color === 'transparent') return color;
    const hex = color.replace('#', '');
    let r: number, g: number, b: number;
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length === 6) {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    } else {
      return color;
    }
    const adjust = (value: number) =>
      percent < 0
        ? Math.round(value * (100 + percent) / 100)
        : Math.round(value + (255 - value) * percent / 100);
    const newR = Math.min(255, Math.max(0, adjust(r)));
    const newG = Math.min(255, Math.max(0, adjust(g)));
    const newB = Math.min(255, Math.max(0, adjust(b)));
    const toHex = (n: number) => n.toString(16).padStart(2, '0');
    return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
  } catch {
    return color;
  }
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function objectRecord(value: unknown): Record<string, unknown> {
  return isObjectRecord(value) ? value : {};
}

function numericDimension(value: number | string | undefined, fallback: number): number {
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  return fallback;
}

export function isLikelyAgentQuery(input: string): boolean {
  const agentPatterns = [
    /\b(what|which|show|list|find|get|tell)\b.*\b(data|sensors?|devices?|readings?|workflows?|automations?|triggers?|schedules?)\b/i,
    /\b(latest|recent|current|last|historical)\b.*\b(readings?|values?|measurements?|data)\b/i,
    /\b(how many|how much|count|total|average|sum|min|max)\b.*\b(sensors?|devices?|readings?|data|workflows?|triggers?|alerts?|events?)\b/i,
    /\b(workflow|automation|pipeline|trigger|schedule)\b.*\b(status|running|active|create|manage)\b/i,
    /\b(who|when|where|why|how)\b.*\b(sensors?|devices?|zones?|areas?|sites?)\b/i,
    /\b(available|connected|online|offline)\b.*\b(sensors?|devices?|data\s+sources?)\b/i,
    /\b(what)\b.*\b(value|reading|measurement|status)\b.*\b(of|for|from)\b/i,
    /\b(what|which|show|get|find|tell)\b.*\b(pressure|temperature|humidity|flow|depth|voltage|level|ph|rpm|speed)\b/i,
  ];
  return agentPatterns.some(pattern => pattern.test(input));
}

export interface UseCanvasTextParserDeps {
  canvasSettings: CanvasSettings;
}

export type ParsedCanvasInsertionPlan =
  | {
      kind: 'singleComponent';
      component: ComponentData;
      message: string;
    }
  | {
      kind: 'multipleComponents';
      components: ComponentData[];
      message: string;
    }
  | {
      kind: 'template';
      templateId: string;
      templateName: string;
      componentCount: number;
      position: { x: number; y: number };
      message: string;
    };

export interface ParsedCanvasCreationResult extends Record<string, unknown> {
  componentCreated?: boolean;
  templateCreated?: boolean;
  type?: ComponentType;
  count?: number;
  templateId?: string;
  templateName?: string;
  componentCount?: number;
  message: string;
  aiCanvasInsertion: ParsedCanvasInsertionPlan;
}

export interface UseCanvasTextParserReturn {
  createMultipleComponents: (type: ComponentType, count: number, positionHint?: string, varyTypes?: boolean) => ComponentData[] | undefined;
  createComponentFromCommand: (type: ComponentType, content?: string, positionHint?: string) => ComponentData | undefined;
  checkAndCreateTemplate: (content: string, lowerContent: string) => ParsedCanvasCreationResult | undefined;
  checkAndCreateComponent: (content: string, lowerContent: string) => ParsedCanvasCreationResult | undefined;
  parseCanvasChanges: (content: string) => Record<string, unknown> | undefined;
}

export function useCanvasTextParser(deps: UseCanvasTextParserDeps): UseCanvasTextParserReturn {
  const { canvasSettings } = deps;

  // ---- Canvas helper functions (moved verbatim from useAgentMessaging.ts) ----

const createMultipleComponents = (type: ComponentType, count: number, positionHint: string = 'auto', varyTypes: boolean = false): ComponentData[] | undefined => {
  const { project, components } = useEditorStore.getState();

  if (!project?.id) {
    console.error('No active project for component creation');
    return undefined;
  }

  const canvasWidth = 1200;
  const margin = 40;
  const spacing = 20;

  // Default sizes
  const defaultSizes: Record<string, { width: number; height: number }> = {
    [ComponentType.TEXT]: { width: 200, height: 50 },
    [ComponentType.BUTTON]: { width: 120, height: 40 },
    [ComponentType.INPUT]: { width: 200, height: 40 },
    [ComponentType.IMAGE]: { width: 300, height: 200 },
    [ComponentType.CONTAINER]: { width: 400, height: 300 },
    [ComponentType.FLEX_CONTAINER]: { width: 400, height: 300 },
    [ComponentType.LIST]: { width: 300, height: 250 },
    [ComponentType.CUSTOM]: { width: 300, height: 200 },
    [ComponentType.TABLE]: { width: 600, height: 400 },
    [ComponentType.ADVANCED_TABLE]: { width: 700, height: 450 },
    [ComponentType.CHART]: { width: 400, height: 300 },
    [ComponentType.BAR_CHART]: { width: 400, height: 300 },
    [ComponentType.LINE_CHART]: { width: 400, height: 300 },
    [ComponentType.PIE_CHART]: { width: 350, height: 350 },
    [ComponentType.SCATTER_CHART]: { width: 400, height: 300 },
    [ComponentType.AREA_CHART]: { width: 400, height: 300 },
    [ComponentType.HEATMAP_CHART]: { width: 450, height: 350 },
    [ComponentType.TREEMAP_CHART]: { width: 450, height: 350 },
    [ComponentType.GAUGE_CHART]: { width: 250, height: 200 },
    [ComponentType.RADIAL_DIAL]: { width: 200, height: 200 },
    [ComponentType.TOGGLE_SWITCH]: { width: 70, height: 46 },
    [ComponentType.STATUS_LIGHT]: { width: 80, height: 100 },
    [ComponentType.PUSH_BUTTON]: { width: 80, height: 80 },
    [ComponentType.ICON_BUTTON]: { width: 60, height: 60 },
    [ComponentType.STEPPER]: { width: 200, height: 60 },
    [ComponentType.SLIDER]: { width: 250, height: 60 },
    [ComponentType.LEVEL_INDICATOR]: { width: 60, height: 150 },
    [ComponentType.SEGMENTED_CONTROL]: { width: 300, height: 50 },
    [ComponentType.COLOR_PICKER]: { width: 200, height: 200 },
    [ComponentType.THERMOSTAT]: { width: 220, height: 260 },
    [ComponentType.KANBAN_BOARD]: { width: 800, height: 500 },
    [ComponentType.GANTT_CHART]: { width: 900, height: 400 },
    [ComponentType.CALENDAR]: { width: 800, height: 600 },
    [ComponentType.QUESTIONNAIRE]: { width: 600, height: 500 },
    [ComponentType.VOTING_CARD]: { width: 500, height: 400 },
    [ComponentType.TIME_TRACKER]: { width: 900, height: 700 },
    [ComponentType.MODAL]: { width: 400, height: 300 },
    [ComponentType.CHECKLIST]: { width: 400, height: 300 },
    [ComponentType.HORIZONTAL_TIMELINE]: { width: 800, height: 200 },
    [ComponentType.ASSIGNMENT_WIDGET]: { width: 400, height: 300 },
  };

  const size = defaultSizes[type] || { width: 200, height: 100 };

  // Default properties per type (no content context — uses static defaults)
  const getDefaultPropertiesForType = (t: ComponentType, _index: number): AnyComponentProperties => {
    switch (t) {
      case ComponentType.TEXT: return { text: 'Sample Text', fontSize: 16, fontWeight: 'normal' };
      case ComponentType.BUTTON: return { label: 'Click Me', variant: 'default' };
      case ComponentType.INPUT: return { placeholder: 'Enter text...', label: 'Input Field', type: 'text' };
      case ComponentType.IMAGE: return { src: 'https://via.placeholder.com/300x200', alt: 'Placeholder Image' };
      case ComponentType.BAR_CHART:
      case ComponentType.LINE_CHART:
      case ComponentType.AREA_CHART:
      case ComponentType.SCATTER_CHART:
      case ComponentType.PIE_CHART:
        return { title: 'Sample Chart', data: [{ label: 'A', value: 30 }, { label: 'B', value: 50 }, { label: 'C', value: 40 }], xAxis: 'Category', yAxis: 'Value' };
      case ComponentType.GAUGE_CHART: return { title: 'Gauge', value: 75, min: 0, max: 100, unit: '%' };
      case ComponentType.MAP_CHART: return { title: 'Map', pins: [] };
      case ComponentType.TABLE:
      case ComponentType.ADVANCED_TABLE:
        return { columns: [], data: [] };
      case ComponentType.FLEX_CONTAINER:
      case ComponentType.CONTAINER:
      case ComponentType.CARD: return { minHeight: 100 };
      default: return {};
    }
  };

  // Calculate starting position based on existing components
  let startX = margin;
  let startY = margin;

  if (components.length > 0) {
    // Find the rightmost/bottommost component
    const lastComponent = components[components.length - 1];
    // Use position if available (deprecated), otherwise default to (0, 0)
    const lastX = lastComponent.position?.x ?? 0;
    const lastY = lastComponent.position?.y ?? 0;
    const lastWidth = numericDimension(lastComponent.size.width, 0);
    const lastHeight = numericDimension(lastComponent.size.height, 0);

    if (positionHint === 'horizontal' || positionHint === 'auto') {
      // Place to the right of last component
      startX = lastX + lastWidth + spacing;
      startY = lastY;

      // Check if we have enough space for all components horizontally
      const totalWidth = count * size.width + (count - 1) * spacing;
      if (startX + totalWidth > canvasWidth - margin) {
        // Start a new row
        startX = margin;
        startY = lastY + lastHeight + spacing;
      }
    } else if (positionHint === 'vertical') {
      // Place below last component
      startX = lastX;
      startY = lastY + lastHeight + spacing;
    }
  }

  // Create components
  try {
    const plannedComponents: ComponentData[] = [];

    for (let i = 0; i < count; i++) {
      let x = startX;
      let y = startY;

      if (positionHint === 'horizontal') {
        x = startX + i * (size.width + spacing);
      } else if (positionHint === 'vertical') {
        y = startY + i * (size.height + spacing);
      } else if (positionHint === 'grid') {
        const cols = Math.ceil(Math.sqrt(count));
        const col = i % cols;
        const row = Math.floor(i / cols);
        x = startX + col * (size.width + spacing);
        y = startY + row * (size.height + spacing);
      } else {
        // Auto layout - horizontal with wrapping
        x = startX + i * (size.width + spacing);
        if (x + size.width > canvasWidth - margin) {
          x = margin;
          y += size.height + spacing;
        }
      }

      // Vary chart types if requested
      let componentType = type;
      if (varyTypes && (type === ComponentType.BAR_CHART || type === ComponentType.CHART)) {
        const chartTypes = [ComponentType.BAR_CHART, ComponentType.LINE_CHART, ComponentType.PIE_CHART, ComponentType.AREA_CHART];
        componentType = chartTypes[i % chartTypes.length];
      }

      const properties = getDefaultPropertiesForType(componentType, i);
      const title = `${componentType.charAt(0).toUpperCase() + componentType.slice(1).replace(/-/g, ' ')} ${Date.now() + i}`;

      plannedComponents.push({
        type: componentType,
        title,
        properties,
        placement: {
          position: { x, y },
          size,
          reasoning: `Planned ${componentType} component from canvas text parser`,
        },
        metadata: {
          generatedByAI: true,
          componentType,
          confidence: 0.9,
          canPlace: true,
        },
      });
    }
    return plannedComponents;
  } catch (error) {
    console.error(`Failed to plan multiple ${type} components:`, error);
    return undefined;
  }
};

const createComponentFromCommand = (type: ComponentType, content?: string, positionHint: string = 'center'): ComponentData | undefined => {
  const { project, components } = useEditorStore.getState();

  if (!project?.id) {
    console.error('No active project for component creation');
    return undefined;
  }

  const canvasWidth = 1200;
  const canvasHeight = 800;

  // Default sizes for different component types
  const defaultSizes: Record<string, { width: number; height: number }> = {
    [ComponentType.TEXT]: { width: 200, height: 50 },
    [ComponentType.BUTTON]: { width: 120, height: 40 },
    [ComponentType.INPUT]: { width: 200, height: 40 },
    [ComponentType.IMAGE]: { width: 300, height: 200 },
    [ComponentType.CONTAINER]: { width: 400, height: 300 },
    [ComponentType.FLEX_CONTAINER]: { width: 400, height: 300 },
    [ComponentType.LIST]: { width: 300, height: 250 },
    [ComponentType.CUSTOM]: { width: 300, height: 200 },
    [ComponentType.TABLE]: { width: 600, height: 400 },
    [ComponentType.ADVANCED_TABLE]: { width: 700, height: 450 },
    [ComponentType.CHART]: { width: 400, height: 300 },
    [ComponentType.BAR_CHART]: { width: 400, height: 300 },
    [ComponentType.LINE_CHART]: { width: 400, height: 300 },
    [ComponentType.PIE_CHART]: { width: 350, height: 350 },
    [ComponentType.SCATTER_CHART]: { width: 400, height: 300 },
    [ComponentType.AREA_CHART]: { width: 400, height: 300 },
    [ComponentType.HEATMAP_CHART]: { width: 450, height: 350 },
    [ComponentType.TREEMAP_CHART]: { width: 450, height: 350 },
    [ComponentType.GAUGE_CHART]: { width: 250, height: 200 },
    [ComponentType.RADIAL_DIAL]: { width: 200, height: 200 },
    [ComponentType.TOGGLE_SWITCH]: { width: 70, height: 46 },
    [ComponentType.STATUS_LIGHT]: { width: 80, height: 100 },
    [ComponentType.PUSH_BUTTON]: { width: 80, height: 80 },
    [ComponentType.ICON_BUTTON]: { width: 60, height: 60 },
    [ComponentType.STEPPER]: { width: 200, height: 60 },
    [ComponentType.SLIDER]: { width: 250, height: 60 },
    [ComponentType.LEVEL_INDICATOR]: { width: 60, height: 150 },
    [ComponentType.SEGMENTED_CONTROL]: { width: 300, height: 50 },
    [ComponentType.COLOR_PICKER]: { width: 200, height: 200 },
    [ComponentType.THERMOSTAT]: { width: 220, height: 260 },
    [ComponentType.KANBAN_BOARD]: { width: 800, height: 500 },
    [ComponentType.GANTT_CHART]: { width: 900, height: 400 },
    [ComponentType.CALENDAR]: { width: 800, height: 600 },
    [ComponentType.QUESTIONNAIRE]: { width: 600, height: 500 },
    [ComponentType.VOTING_CARD]: { width: 500, height: 400 },
    [ComponentType.TIME_TRACKER]: { width: 900, height: 700 },
    [ComponentType.MODAL]: { width: 400, height: 300 },
    [ComponentType.CHECKLIST]: { width: 400, height: 300 },
    [ComponentType.HORIZONTAL_TIMELINE]: { width: 800, height: 200 },
    [ComponentType.ASSIGNMENT_WIDGET]: { width: 400, height: 300 },
  };

  // Get default properties for component type
  const getDefaultProperties = (type: ComponentType): AnyComponentProperties => {
    switch (type) {
      case ComponentType.TEXT:
        return { text: content || 'Sample Text', fontSize: 16, fontWeight: 'normal' };
      case ComponentType.BUTTON:
        return { label: content || 'Click Me', variant: 'default', onClick: '' };
      case ComponentType.INPUT:
        return { placeholder: content || 'Enter text...', label: 'Input Field', type: 'text' };
      case ComponentType.IMAGE:
        return { src: 'https://via.placeholder.com/300x200', alt: content || 'Placeholder Image' };
      case ComponentType.BAR_CHART:
      case ComponentType.LINE_CHART:
      case ComponentType.PIE_CHART:
      case ComponentType.AREA_CHART:
      case ComponentType.SCATTER_CHART:
        return {
          title: content || 'Sample Chart',
          data: [
            { label: 'Jan', value: 30 },
            { label: 'Feb', value: 45 },
            { label: 'Mar', value: 60 },
            { label: 'Apr', value: 35 },
            { label: 'May', value: 70 }
          ],
          xAxis: 'Month',
          yAxis: 'Value'
        };
      case ComponentType.HEATMAP_CHART:
      case ComponentType.TREEMAP_CHART:
        return {
          title: content || 'Sample Chart',
          data: [
            { name: 'Category A', value: 100 },
            { name: 'Category B', value: 80 },
            { name: 'Category C', value: 60 },
            { name: 'Category D', value: 40 }
          ]
        };
      case ComponentType.GAUGE_CHART:
        return {
          title: content || 'Performance',
          value: 75,
          min: 0,
          max: 100,
          unit: '%'
        };
      case ComponentType.RADIAL_DIAL:
        return {
          title: content || 'Control',
          value: 75,
          min: 0,
          max: 100,
          step: 1,
          unit: '%',
          theme: 'neon-glow',
          isOn: true,
          arcStartAngle: 135,
          arcEndAngle: 405,
        };
      case ComponentType.TOGGLE_SWITCH:
        return {
          isOn: true,
          label: content || 'Switch',
          theme: 'ios',
          size: 'md',
        };
      case ComponentType.STATUS_LIGHT:
        return {
          status: 'ok',
          label: content || 'Status',
          theme: 'led',
          shape: 'circle',
          size: 'md',
          blinkMode: 'none',
          showLabel: true,
        };
      case ComponentType.PUSH_BUTTON:
        return {
          label: content || 'Button',
          theme: 'power',
          shape: 'circular',
          size: 'md',
          releaseColor: '#2a2a2a',
          pressColor: '#ff3333',
          glowColor: '#ff0000',
          glowIntensity: 8,
        };
      case ComponentType.ICON_BUTTON:
        return {
          iconName: 'Power',
          label: content || '',
          theme: 'circular',
          isActive: false,
          shape: 'circular',
          iconSize: 24,
          showLabel: false,
        };
      case ComponentType.STEPPER:
        return {
          value: 0,
          min: 0,
          max: 100,
          step: 1,
          unit: '',
          decimals: 0,
          label: content || '',
          theme: 'flat',
          size: 'md',
          longPressAcceleration: true,
        };
      case ComponentType.SLIDER:
        return {
          value: 50,
          min: 0,
          max: 100,
          step: 1,
          unit: '',
          decimals: 0,
          orientation: 'horizontal',
          showValue: true,
          showMinMax: false,
          showTicks: false,
          label: content || '',
          theme: 'flat',
        };
      case ComponentType.LEVEL_INDICATOR:
        return {
          value: 75,
          segments: 10,
          orientation: 'vertical',
          showValue: false,
          showPercentage: false,
          label: content || '',
          unit: '',
          min: 0,
          max: 100,
          theme: 'battery',
        };
      case ComponentType.SEGMENTED_CONTROL:
        return {
          segments: [
            { id: 'opt1', label: 'Option 1' },
            { id: 'opt2', label: 'Option 2' },
            { id: 'opt3', label: 'Option 3' },
          ],
          selectedIndex: 0,
          theme: 'ios',
        };
      case ComponentType.COLOR_PICKER:
        return {
          color: '#FF0000',
          mode: 'wheel',
          showHex: true,
          showBrightness: true,
          theme: 'default',
        };
      case ComponentType.THERMOSTAT:
        return {
          temperature: 72,
          targetTemperature: 72,
          min: 50,
          max: 90,
          unit: 'fahrenheit',
          mode: 'auto',
          modes: ['auto', 'heat', 'cool', 'fan', 'off'],
          humidity: 45,
          showHumidity: true,
          theme: 'nest',
        };
      case ComponentType.TABLE:
      case ComponentType.ADVANCED_TABLE:
        return {
          columns: [
            { header: 'Name', accessorKey: 'name', enableSorting: true },
            { header: 'Value', accessorKey: 'value', enableSorting: true },
            { header: 'Status', accessorKey: 'status', enableSorting: true },
          ],
          data: [
            { name: 'Item 1', value: 100, status: 'Active' },
            { name: 'Item 2', value: 200, status: 'Pending' },
            { name: 'Item 3', value: 150, status: 'Active' },
          ],
          enableSorting: true,
          striped: true,
          hoverable: true,
        };
      case ComponentType.LIST:
        return {
          items: [
            { id: '1', text: 'First item' },
            { id: '2', text: 'Second item' },
            { id: '3', text: 'Third item' }
          ],
          ordered: false
        };
      case ComponentType.CONTAINER:
      case ComponentType.FLEX_CONTAINER:
        return {
          layout: type === ComponentType.FLEX_CONTAINER ? 'flex' : 'block',
          direction: 'row',
          justify: 'flex-start',
          align: 'flex-start',
          gap: 10
        };
      case ComponentType.FILE_STORAGE:
        return {
          title: content || 'File Storage',
          displayMode: 'grid',
          uploadEnabled: true,
          deleteEnabled: true,
          showSearch: true,
          showThumbnails: true,
          maxFileSize: 10485760,
          allowedFileTypes: ['image/*', 'application/pdf'],
          defaultScope: 'project',
        };
      case ComponentType.KANBAN_BOARD:
        return {
          columns: [
            { id: 'todo', label: 'To Do', color: '#6B7280' },
            { id: 'in-progress', label: 'In Progress', color: '#3B82F6' },
            { id: 'done', label: 'Done', color: '#10B981' },
          ],
          cardLayout: 'standard',
          fieldMapping: { title: 'title', column: 'status' },
          dragEnabled: true,
          showColumnCounts: true,
          sampleData: [
            { id: '1', title: 'Design homepage', status: 'todo', priority: 'high', assignee: 'Alice' },
            { id: '2', title: 'Fix login bug', status: 'in-progress', priority: 'high', assignee: 'Bob' },
            { id: '3', title: 'Update docs', status: 'in-progress', priority: 'low', assignee: 'Alice' },
            { id: '4', title: 'Add dark mode', status: 'todo', priority: 'medium', assignee: 'Charlie' },
            { id: '5', title: 'Write tests', status: 'done', priority: 'medium', assignee: 'Bob' },
            { id: '6', title: 'Deploy v2.0', status: 'done', priority: 'high', assignee: 'Alice' },
          ],
        };
      case ComponentType.GANTT_CHART:
        return {
          timeScale: 'week',
          showDependencies: true,
          showProgress: true,
          showMilestones: true,
          showTodayLine: true,
          showGridLines: true,
          rowHeight: 36,
          barHeight: 24,
          dragEnabled: true,
          resizeEnabled: true,
          groupBy: 'group',
          fieldMapping: {
            id: 'id',
            taskName: 'taskName',
            startDate: 'startDate',
            endDate: 'endDate',
            progress: 'progress',
            dependencies: 'dependencies',
            group: 'group',
            milestone: 'milestone',
            assignee: 'assignee',
          },
          sampleData: [
            { id: '1', taskName: 'Requirements Gathering', startDate: '2026-04-01', endDate: '2026-04-14', progress: 100, group: 'Design', assignee: 'Alice' },
            { id: '2', taskName: 'Wireframes', startDate: '2026-04-10', endDate: '2026-04-21', progress: 60, dependencies: '1', group: 'Design', assignee: 'Bob' },
            { id: '3', taskName: 'API Development', startDate: '2026-04-15', endDate: '2026-05-05', progress: 30, dependencies: '1', group: 'Development', assignee: 'Carol' },
            { id: '4', taskName: 'Frontend Build', startDate: '2026-04-22', endDate: '2026-05-12', progress: 0, dependencies: '2,3', group: 'Development', assignee: 'Dave' },
            { id: '5', taskName: 'QA Testing', startDate: '2026-05-06', endDate: '2026-05-19', progress: 0, dependencies: '3', group: 'Testing', assignee: 'Eve' },
            { id: '6', taskName: 'Launch', startDate: '2026-05-20', endDate: '2026-05-20', progress: 0, dependencies: '4,5', group: 'Testing', milestone: true },
          ],
        };
      case ComponentType.CALENDAR:
        return {
          viewMode: 'month',
          firstDayOfWeek: 1,
          showWeekends: true,
          showWeekNumbers: false,
          showEventTime: true,
          businessHoursStart: 9,
          businessHoursEnd: 17,
          timeSlotInterval: 60,
          maxEventsPerDay: 3,
          dragEnabled: true,
          inlineCreateEnabled: false,
          fieldMapping: {
            id: 'id',
            title: 'title',
            startDate: 'start',
            endDate: 'end',
            allDay: 'allDay',
            category: 'category',
            color: 'color',
            description: 'description',
            location: 'location',
          },
          sampleData: [
            { id: '1', title: 'Team Meeting', start: '2026-04-14T09:00', end: '2026-04-14T10:00', category: 'meeting' },
            { id: '2', title: 'Sprint Demo', start: '2026-04-14T14:00', end: '2026-04-14T15:00', category: 'meeting' },
            { id: '3', title: 'Feature Freeze', start: '2026-04-08', end: '2026-04-12', category: 'milestone', allDay: true },
            { id: '4', title: 'Deploy v2.1', start: '2026-04-16T11:00', end: '2026-04-16T12:00', category: 'release' },
            { id: '5', title: 'Design Review', start: '2026-04-15T10:00', end: '2026-04-15T11:30', category: 'review' },
            { id: '6', title: 'QA Signoff', start: '2026-04-18T16:00', end: '2026-04-18T17:00', category: 'milestone' },
          ],
        };
      case ComponentType.QUESTIONNAIRE:
        return {
          title: content || 'Questionnaire',
          steps: [
            { id: 'step-1', title: 'Welcome', order: 0, questions: [
              { id: 'q1', type: 'text', label: 'What is your name?', required: true }
            ]},
            { id: 'step-2', title: 'Preferences', order: 1, questions: [
              { id: 'q2', type: 'single-select', label: 'Pick your favorite', required: true, options: [
                { value: 'a', label: 'Option A' }, { value: 'b', label: 'Option B' }, { value: 'c', label: 'Option C' }
              ]}
            ]},
            { id: 'step-3', title: 'Feedback', order: 2, questions: [
              { id: 'q3', type: 'rating', label: 'Rate your experience', required: false }
            ]}
          ],
          viewMode: 'form',
          showProgress: true,
          allowSkip: false,
          allowBackNavigation: true,
          questionLayout: 'stacked',
          requireAuth: false,
          allowMultipleSubmissions: false,
        };
      case ComponentType.VOTING_CARD:
        return {
          title: content || 'Vote',
          description: 'Cast your vote on the options below',
          options: [
            { id: 'opt-1', label: 'Option A', description: 'First choice' },
            { id: 'opt-2', label: 'Option B', description: 'Second choice' },
            { id: 'opt-3', label: 'Option C', description: 'Third choice' },
          ],
          voteType: 'approve-veto',
          resolution: { type: 'majority', timeoutAction: 'extend' },
          showResults: 'after-vote',
          showVoterNames: true,
          allowChangeVote: true,
          notifyOnComplete: false,
          optionLayout: 'list',
        };
      case ComponentType.TIME_TRACKER:
        return {
          viewMode: 'timer',
          defaultBillable: true,
          currency: 'USD',
          timeRounding: 15,
          showBillableToggle: true,
          showTags: true,
          projects: [],
          clients: [],
          tasks: [],
        };
      case ComponentType.MODAL:
        return {
          title: 'Modal',
          triggerText: 'Open Modal',
          triggerVariant: 'primary',
          size: 'md',
          showBackdrop: true,
          closeOnBackdropClick: true,
          showCloseButton: true,
          animationType: 'fade',
        };
      case ComponentType.CHECKLIST:
        return {
          title: 'Checklist',
          items: [
            { id: '1', label: 'Define target audience', checked: false, priority: 'high' },
            { id: '2', label: 'Create landing page', checked: false, priority: 'medium' },
            { id: '3', label: 'Set up analytics', checked: true, priority: 'medium' },
            { id: '4', label: 'Write launch email', checked: true, priority: 'low' },
            { id: '5', label: 'Prepare social media assets', checked: false, priority: 'low' },
          ],
          showProgress: true,
          allowReorder: false,
        };
      case ComponentType.HORIZONTAL_TIMELINE:
        return {
          events: [
            { id: '1', title: 'Research & Planning', status: 'completed', startDate: '2025-01-01', endDate: '2025-03-31' },
            { id: '2', title: 'MVP Launch', status: 'active', startDate: '2025-04-01', endDate: '2025-06-30' },
            { id: '3', title: 'Growth Phase', status: 'upcoming', startDate: '2025-07-01', endDate: '2025-09-30' },
            { id: '4', title: 'Scale', status: 'upcoming', startDate: '2025-10-01', endDate: '2025-12-31' },
          ],
          orientation: 'horizontal',
          showConnectors: true,
          showDates: true,
          currentEventId: '2',
          variant: 'milestone',
          compact: false,
        };
      case ComponentType.ASSIGNMENT_WIDGET:
        return {
          assignments: [
            { id: '1', name: 'Alice Chen', role: 'Product Lead', avatar: '', tasks: [{ title: 'Define roadmap', priority: 'high', completed: true }, { title: 'User research', priority: 'medium', completed: false }, { title: 'Stakeholder review', priority: 'high', completed: false }], status: 'available' },
            { id: '2', name: 'Bob Martinez', role: 'Designer', avatar: '', tasks: [{ title: 'Wireframes', priority: 'high', completed: true }, { title: 'Prototype', priority: 'medium', completed: false }], status: 'busy' },
            { id: '3', name: 'Carol Singh', role: 'Engineer', avatar: '', tasks: [{ title: 'API scaffolding', priority: 'high', completed: true }, { title: 'Frontend build', priority: 'high', completed: false }, { title: 'Testing', priority: 'medium', completed: false }, { title: 'Deploy', priority: 'low', completed: false }], status: 'available' },
          ],
          layout: 'list',
          showTaskCount: true,
          showStatus: true,
        };
      default:
        return {};
    }
  };

  // Calculate position based on hint
  const getPosition = (hint: string): { x: number; y: number } => {
    const size = defaultSizes[type] || { width: 200, height: 100 };
    const margin = 40;

    const positions: Record<string, { x: number; y: number }> = {
      'center': {
        x: (canvasWidth - size.width) / 2,
        y: (canvasHeight - size.height) / 2
      },
      'top': {
        x: (canvasWidth - size.width) / 2,
        y: margin
      },
      'bottom': {
        x: (canvasWidth - size.width) / 2,
        y: canvasHeight - size.height - margin
      },
      'left': {
        x: margin,
        y: (canvasHeight - size.height) / 2
      },
      'right': {
        x: canvasWidth - size.width - margin,
        y: (canvasHeight - size.height) / 2
      },
      'top-left': { x: margin, y: margin },
      'top-right': {
        x: canvasWidth - size.width - margin,
        y: margin
      },
      'bottom-left': {
        x: margin,
        y: canvasHeight - size.height - margin
      },
      'bottom-right': {
        x: canvasWidth - size.width - margin,
        y: canvasHeight - size.height - margin
      }
    };

    let position = positions[hint] || positions['center'];

    // Simple collision detection - find a non-overlapping position
    const spacing = 20;
    let attempts = 0;
    while (attempts < 20) {
      const overlaps = components.some((comp: Component) => {
        // Use optional chaining for deprecated position property
        const compX = comp.position?.x ?? 0;
        const compY = comp.position?.y ?? 0;
        const compWidth = numericDimension(comp.size.width, 0);
        const compHeight = numericDimension(comp.size.height, 0);
        return (
          position.x < compX + compWidth &&
          position.x + size.width > compX &&
          position.y < compY + compHeight &&
          position.y + size.height > compY
        );
      });

      if (!overlaps) break;

      // Try different positions
      position.x += spacing;
      if (position.x + size.width > canvasWidth - margin) {
        position.x = margin;
        position.y += spacing;
      }
      attempts++;
    }

    return position;
  };

  try {
    const position = getPosition(positionHint);
    const size = defaultSizes[type] || { width: 200, height: 100 };
    const properties = getDefaultProperties(type);
    const title = `${type.charAt(0).toUpperCase() + type.slice(1).replace(/-/g, ' ')} ${Date.now()}`;

    return {
      type,
      title,
      properties,
      placement: {
        position,
        size,
        reasoning: `Planned ${type} component from canvas text parser`,
      },
      metadata: {
        generatedByAI: true,
        componentType: type,
        confidence: 0.9,
        canPlace: true,
      },
    };
  } catch (error) {
    console.error(`Failed to plan ${type} component:`, error);
    return undefined;
  }
};

// Helper function to create multiple components

const checkAndCreateTemplate = (content: string, _lowerContent: string): ParsedCanvasCreationResult | undefined => {
  // Check for template patterns
  const templatePatterns = [
    { pattern: /(add|create|insert|place|make|give me)\s+(a\s+)?(two[-\s]?column|sidebar)\s*(layout|template)?/i },
    { pattern: /(add|create|insert|place|make|give me)\s+(a\s+)?hero\s*(section|layout|template)?/i },
    { pattern: /(add|create|insert|place|make|give me)\s+(a\s+)?card\s+grid/i },
    { pattern: /(add|create|insert|place|make|give me)\s+(a\s+)?dashboard/i },
    { pattern: /(add|create|insert|place|make|give me)\s+(a\s+)?header/i },
    { pattern: /(add|create|insert|place|make|give me)\s+(a\s+)?footer/i },
  ];

  for (const { pattern } of templatePatterns) {
    const match = content.match(pattern);
    if (match) {
      // Try to match a template
      const template = templateInstantiationService.matchTemplatePattern(content);
      if (template) {
        const message = `✅ Creating ${template.metadata.name} template with ${template.metadata.componentCount} components. You can now select individual components to customize them.`;
        return {
          templateCreated: true,
          templateId: template.metadata.id,
          templateName: template.metadata.name,
          componentCount: template.metadata.componentCount,
          message,
          aiCanvasInsertion: {
            kind: 'template',
            templateId: template.metadata.id,
            templateName: template.metadata.name,
            componentCount: template.metadata.componentCount,
            position: { x: 50, y: 50 },
            message,
          },
        };
      }
    }
  }

  return undefined;
};

// Helper function to check and create components

const checkAndCreateComponent = (content: string, lowerContent: string): ParsedCanvasCreationResult | undefined => {
  const { project } = useEditorStore.getState();

  if (!project?.id) {
    console.error('No active project for component creation');
    return undefined;
  }

  // Check for multiple component creation
  const multiplePatterns = [
    { pattern: /(add|create|insert|place|make|give me)\s+(\d+|two|three|four|five|several|some|a few)\s+(more\s+)?(chart|graph|button|text|input|table|container|image|list|bar chart|line chart|pie chart)s?/i },
    { pattern: /(\d+|two|three|four|five|several|some|a few)\s+(more\s+)?(chart|graph|button|text|input|table|container|image|list|bar chart|line chart|pie chart)s?/i },
    { pattern: /(another|one more|more)\s+(chart|graph|button|text|input|table|container|image|list|bar chart|line chart|pie chart)s?/i },
  ];

  for (const { pattern } of multiplePatterns) {
    const match = content.match(pattern);
    if (match) {
      let countStr = match[2] || match[1];
      const componentTypeStr = (match[4] || match[3] || match[2]).toLowerCase();

      // Handle special count words
      const numberMap: Record<string, number> = {
        'two': 2, 'three': 3, 'four': 4, 'five': 5,
        'several': 3, 'some': 2, 'a few': 2,
        'another': 1, 'one more': 1, 'more': 2
      };

      // Check if it's "another" or "one more" pattern
      if (match[1] && (match[1].toLowerCase() === 'another' || match[1].toLowerCase() === 'one more' || match[1].toLowerCase() === 'more')) {
        countStr = match[1].toLowerCase();
      }

      const count = numberMap[countStr.toLowerCase()] || parseInt(countStr, 10) || 1;

      // Determine component type with more specific chart types
      let type = ComponentType.TEXT;
      if (componentTypeStr.includes('bar chart')) {
        type = ComponentType.BAR_CHART;
      } else if (componentTypeStr.includes('line chart') || componentTypeStr.includes('line graph')) {
        type = ComponentType.LINE_CHART;
      } else if (componentTypeStr.includes('pie chart')) {
        type = ComponentType.PIE_CHART;
      } else if (componentTypeStr.includes('chart') || componentTypeStr.includes('graph')) {
        // Mix different chart types when creating multiple
        const chartTypes = [ComponentType.BAR_CHART, ComponentType.LINE_CHART, ComponentType.PIE_CHART];
        type = chartTypes[0]; // Default to bar, but we'll vary this in creation
      } else if (componentTypeStr.includes('button')) {
        type = ComponentType.BUTTON;
      } else if (componentTypeStr.includes('text')) {
        type = ComponentType.TEXT;
      } else if (componentTypeStr.includes('input')) {
        type = ComponentType.INPUT;
      } else if (componentTypeStr.includes('table')) {
        type = ComponentType.TABLE;
      } else if (componentTypeStr.includes('image')) {
        type = ComponentType.IMAGE;
      } else if (componentTypeStr.includes('container')) {
        type = ComponentType.FLEX_CONTAINER;
      } else if (componentTypeStr.includes('list')) {
        type = ComponentType.LIST;
      }

      // Determine positioning
      let positionHint = 'auto';
      if (lowerContent.includes('same row') || lowerContent.includes('horizontally') || lowerContent.includes('in a row')) {
        positionHint = 'horizontal';
      } else if (lowerContent.includes('same column') || lowerContent.includes('vertically') || lowerContent.includes('in a column')) {
        positionHint = 'vertical';
      } else if (lowerContent.includes('grid')) {
        positionHint = 'grid';
      }

      // Check if we should vary chart types
      const shouldVaryCharts = componentTypeStr.includes('chart') && !componentTypeStr.includes('bar') && !componentTypeStr.includes('line') && !componentTypeStr.includes('pie');

      const plannedComponents = createMultipleComponents(type, count, positionHint, shouldVaryCharts);
      if (plannedComponents?.length) {
        const message = `✅ Created ${count} ${shouldVaryCharts ? 'chart' : type} component${count > 1 ? 's' : ''}`;
        return {
          componentCreated: true,
          type,
          count,
          message,
          aiCanvasInsertion: {
            kind: 'multipleComponents',
            components: plannedComponents,
            message,
          },
        };
      }
    }
  }

  const componentPatterns = [
    // Basic components
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?text/i, type: ComponentType.TEXT },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?button/i, type: ComponentType.BUTTON },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?input/i, type: ComponentType.INPUT },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?image/i, type: ComponentType.IMAGE },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?container/i, type: ComponentType.FLEX_CONTAINER },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?flex[\s-]?container/i, type: ComponentType.FLEX_CONTAINER },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?list/i, type: ComponentType.LIST },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?custom/i, type: ComponentType.CUSTOM },

    // Kanban boards
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?kanban(\s+board)?/i, type: ComponentType.KANBAN_BOARD },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?task\s+board/i, type: ComponentType.KANBAN_BOARD },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?(sales\s+)?pipeline(\s+board)?/i, type: ComponentType.KANBAN_BOARD },

    // Gantt charts
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?gantt(\s+chart)?/i, type: ComponentType.GANTT_CHART },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?project\s+timeline/i, type: ComponentType.GANTT_CHART },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?task\s+timeline/i, type: ComponentType.GANTT_CHART },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?schedule\s+chart/i, type: ComponentType.GANTT_CHART },

    // Calendars
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?(event\s+)?calendar/i, type: ComponentType.CALENDAR },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?schedule[r]?\b/i, type: ComponentType.CALENDAR },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?appointment\s+(calendar|scheduler)/i, type: ComponentType.CALENDAR },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?day\s+planner/i, type: ComponentType.CALENDAR },

    // Questionnaires
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?questionnaire/i, type: ComponentType.QUESTIONNAIRE },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?survey/i, type: ComponentType.QUESTIONNAIRE },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?quiz/i, type: ComponentType.QUESTIONNAIRE },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?(intake|onboarding|feedback)\s+form/i, type: ComponentType.QUESTIONNAIRE },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?preference\s+(survey|form)/i, type: ComponentType.QUESTIONNAIRE },

    // Voting Cards
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?voting\s*card/i, type: ComponentType.VOTING_CARD },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?vote/i, type: ComponentType.VOTING_CARD },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?poll/i, type: ComponentType.VOTING_CARD },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?ballot/i, type: ComponentType.VOTING_CARD },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?approval\s*card/i, type: ComponentType.VOTING_CARD },

    // Time Tracker
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?time\s*track(er|ing)/i, type: ComponentType.TIME_TRACKER },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?time\s*sheet/i, type: ComponentType.TIME_TRACKER },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?hours?\s*tracker/i, type: ComponentType.TIME_TRACKER },

    // Tables
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?table/i, type: ComponentType.TABLE },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(an?\s+)?advanced\s+table/i, type: ComponentType.ADVANCED_TABLE },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?data\s+table/i, type: ComponentType.ADVANCED_TABLE },

    // Charts - specific types
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?bar\s+(chart|graph)/i, type: ComponentType.BAR_CHART },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?line\s+(chart|graph)/i, type: ComponentType.LINE_CHART },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?pie\s+(chart|graph)/i, type: ComponentType.PIE_CHART },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?scatter\s+(chart|plot|graph)/i, type: ComponentType.SCATTER_CHART },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(an?\s+)?area\s+(chart|graph)/i, type: ComponentType.AREA_CHART },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?heatmap/i, type: ComponentType.HEATMAP_CHART },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?treemap/i, type: ComponentType.TREEMAP_CHART },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?(radial\s+)?dial/i, type: ComponentType.RADIAL_DIAL },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?dimmer/i, type: ComponentType.RADIAL_DIAL },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?radial\s+(control|slider)/i, type: ComponentType.RADIAL_DIAL },
    { pattern: /(add|create|insert|place|put|drop|new)\s+(a\s+)?toggle(\s+switch)?/i, type: ComponentType.TOGGLE_SWITCH },
    { pattern: /(add|create|insert|place|put|drop|new)\s+(a\s+)?(on\s*\/?\s*off\s+)?switch/i, type: ComponentType.TOGGLE_SWITCH },
    { pattern: /(add|create|insert|place|put|drop|new)\s+(a\s+)?status\s+(light|indicator|led)/i, type: ComponentType.STATUS_LIGHT },
    { pattern: /(add|create|insert|place|put|drop|new)\s+(a\s+)?(led|signal)\s+(light|indicator)/i, type: ComponentType.STATUS_LIGHT },
    { pattern: /(add|create|insert|place|put|drop|new)\s+(a\s+)?push\s+button/i, type: ComponentType.PUSH_BUTTON },
    { pattern: /(add|create|insert|place|put|drop|new)\s+(a\s+)?momentary\s+button/i, type: ComponentType.PUSH_BUTTON },
    { pattern: /(add|create|insert|place|put|drop|new)\s+(a\s+)?press\s+button/i, type: ComponentType.PUSH_BUTTON },
    { pattern: /(add|create|insert|place|put|drop|new)\s+(a\s+)?icon\s+button/i, type: ComponentType.ICON_BUTTON },
    { pattern: /(add|create|insert|place|put|drop|new)\s+(a\s+)?icon\s+toggle/i, type: ComponentType.ICON_BUTTON },
    { pattern: /(add|create|insert|place|put|drop|new)\s+(a\s+)?stepper/i, type: ComponentType.STEPPER },
    { pattern: /(add|create|insert|place|put|drop|new)\s+(a\s+)?(?:number\s+)?(?:incrementor|counter)/i, type: ComponentType.STEPPER },
    { pattern: /(add|create|insert|place|put|drop|new)\s+(a\s+)?plus\s*\/?\s*minus/i, type: ComponentType.STEPPER },
    { pattern: /(add|create|insert|place|put|drop|new)\s+(a\s+)?(?:range\s+)?slider/i, type: ComponentType.SLIDER },
    { pattern: /(add|create|insert|place|put|drop|new)\s+(a\s+)?slide\s+control/i, type: ComponentType.SLIDER },
    { pattern: /(add|create|insert|place|put|drop|new)\s+(a\s+)?range\s+control/i, type: ComponentType.SLIDER },
    { pattern: /(add|create|insert|place|put|drop|new)\s+(a\s+)?level\s+indicator/i, type: ComponentType.LEVEL_INDICATOR },
    { pattern: /(add|create|insert|place|put|drop|new)\s+(a\s+)?battery\s+(?:indicator|gauge)/i, type: ComponentType.LEVEL_INDICATOR },
    { pattern: /(add|create|insert|place|put|drop|new)\s+(a\s+)?signal\s+(?:meter|bars?)/i, type: ComponentType.LEVEL_INDICATOR },
    { pattern: /(add|create|insert|place|put|drop|new)\s+(a\s+)?level\s+(?:gauge|bar|meter)/i, type: ComponentType.LEVEL_INDICATOR },
    { pattern: /(add|create|insert|place|make|put|drop)\s+(a\s+)?segmented\s*(control)?/i, type: ComponentType.SEGMENTED_CONTROL },
    { pattern: /(add|create|insert|place|make|put|drop)\s+(a\s+)?tab\s*(bar|selector)/i, type: ComponentType.SEGMENTED_CONTROL },
    { pattern: /(add|create|insert|place|make|put|drop)\s+(a\s+)?colou?r\s*(pick|wheel|select|choos)/i, type: ComponentType.COLOR_PICKER },
    { pattern: /(add|create|insert|place|make|put|drop)\s+(a\s+)?thermostat/i, type: ComponentType.THERMOSTAT },
    { pattern: /(add|create|insert|place|make|put|drop)\s+(a\s+)?hvac\s*control/i, type: ComponentType.THERMOSTAT },
    { pattern: /(add|create|insert|place|make|put|drop)\s+(a\s+)?temp(?:erature)?\s*control/i, type: ComponentType.THERMOSTAT },
    { pattern: /(add|create|insert|place|make|put|drop)\s+(a\s+)?climate\s*control/i, type: ComponentType.THERMOSTAT },
    { pattern: /(add|create|insert|place|make|put|drop)\s+(a\s+)?modal/i, type: ComponentType.MODAL },
    { pattern: /(add|create|insert|place|make|put|drop)\s+(a\s+)?dialog/i, type: ComponentType.MODAL },
    { pattern: /(add|create|insert|place|make|put|drop)\s+(a\s+)?popup/i, type: ComponentType.MODAL },
    { pattern: /(add|create|insert|place|make|put|drop)\s+(a\s+)?checklist/i, type: ComponentType.CHECKLIST },
    { pattern: /(add|create|insert|place|make|put|drop)\s+(a\s+)?todo\s*list/i, type: ComponentType.CHECKLIST },
    { pattern: /(add|create|insert|place|make|put|drop)\s+(a\s+)?task\s*list/i, type: ComponentType.CHECKLIST },
    { pattern: /(add|create|insert|place|make|put|drop)\s+(a\s+)?timeline/i, type: ComponentType.HORIZONTAL_TIMELINE },
    { pattern: /(add|create|insert|place|make|put|drop)\s+(a\s+)?gantt/i, type: ComponentType.HORIZONTAL_TIMELINE },
    { pattern: /(add|create|insert|place|make|put|drop)\s+(a\s+)?roadmap/i, type: ComponentType.HORIZONTAL_TIMELINE },
    { pattern: /(add|create|insert|place|make|put|drop)\s+(a\s+)?project\s*timeline/i, type: ComponentType.HORIZONTAL_TIMELINE },
    { pattern: /(add|create|insert|place|make|put|drop)\s+(a\s+)?assignment\s*widget/i, type: ComponentType.ASSIGNMENT_WIDGET },
    { pattern: /(add|create|insert|place|make|put|drop)\s+(a\s+)?team\s*(assignment|widget|roster)/i, type: ComponentType.ASSIGNMENT_WIDGET },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?gauge/i, type: ComponentType.GAUGE_CHART },

    // Generic chart (defaults to bar chart)
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?chart(?!\s+(chart|graph))/i, type: ComponentType.BAR_CHART },
    { pattern: /(add|create|insert|place|make|put|can you add|can you create|can you make|can you place|give me|i need|i want)\s+(a\s+)?graph(?!\s+(chart|graph))/i, type: ComponentType.BAR_CHART },

    // Shorthand versions
    { pattern: /^(text|add text|new text)$/i, type: ComponentType.TEXT },
    { pattern: /^(button|add button|new button)$/i, type: ComponentType.BUTTON },
    { pattern: /^(input|add input|new input)$/i, type: ComponentType.INPUT },
    { pattern: /^(chart|add chart|new chart)$/i, type: ComponentType.BAR_CHART },
    { pattern: /^(table|add table|new table)$/i, type: ComponentType.TABLE },
    { pattern: /^(image|add image|new image)$/i, type: ComponentType.IMAGE },
    { pattern: /^(container|add container|new container)$/i, type: ComponentType.FLEX_CONTAINER },
    { pattern: /^(list|add list|new list)$/i, type: ComponentType.LIST },
    { pattern: /^(kanban|add kanban|new kanban|kanban board|add kanban board)$/i, type: ComponentType.KANBAN_BOARD },
    { pattern: /^(modal|add modal|new modal|dialog|add dialog|popup|add popup)$/i, type: ComponentType.MODAL },
  ];

  // Check for component creation patterns
  for (const { pattern, type } of componentPatterns) {
    const match = content.match(pattern);
    if (match) {
      // Extract content/label from the command if provided
      let componentContent: string | undefined;
      const contentPatterns = [
        /(?:that\s+says|with\s+text|labeled|called|named|titled)\s+["']?([^"']+)["']?/i,
        /["']([^"']+)["']/
      ];

      for (const contentPattern of contentPatterns) {
        const contentMatch = content.match(contentPattern);
        if (contentMatch) {
          componentContent = contentMatch[1];
          break;
        }
      }

      // Determine position hint from command
      let positionHint = 'center';
      if (lowerContent.includes('top left') || lowerContent.includes('upper left')) {
        positionHint = 'top-left';
      } else if (lowerContent.includes('top right') || lowerContent.includes('upper right')) {
        positionHint = 'top-right';
      } else if (lowerContent.includes('bottom left') || lowerContent.includes('lower left')) {
        positionHint = 'bottom-left';
      } else if (lowerContent.includes('bottom right') || lowerContent.includes('lower right')) {
        positionHint = 'bottom-right';
      } else if (lowerContent.includes('at the top') || lowerContent.includes('on top')) {
        positionHint = 'top';
      } else if (lowerContent.includes('at the bottom') || lowerContent.includes('on bottom')) {
        positionHint = 'bottom';
      } else if (lowerContent.includes('on the left') || lowerContent.includes('at the left')) {
        positionHint = 'left';
      } else if (lowerContent.includes('on the right') || lowerContent.includes('at the right')) {
        positionHint = 'right';
      } else if (lowerContent.includes('in the center') || lowerContent.includes('centered')) {
        positionHint = 'center';
      }

      const plannedComponent = createComponentFromCommand(type, componentContent, positionHint);

      if (plannedComponent) {
        const message = `✅ Created ${type} component`;
        return {
          componentCreated: true,
          type,
          message,
          aiCanvasInsertion: {
            kind: 'singleComponent',
            component: plannedComponent,
            message,
          },
        };
      }
    }
  }

  return undefined;
};

// Helper function to create a single component

const parseCanvasChanges = (content: string): Record<string, unknown> | undefined => {
  const changes: Record<string, unknown> = {};
  const lowerContent = content.toLowerCase();

  // Check for template patterns first
  const templateResult = checkAndCreateTemplate(content, lowerContent);
  if (templateResult) {
    return templateResult;
  }

  // Check for component creation commands
  const componentResult = checkAndCreateComponent(content, lowerContent);
  if (componentResult) {
    return componentResult;
  }

  // Get current background color for relative adjustments
  const currentBgColor = canvasSettings?.background?.color || '#FFFFFF';

  // Natural language questions and descriptions
  // Convert questions to commands
  const normalizedContent = lowerContent
    .replace(/can you make it/g, '')
    .replace(/could you make it/g, '')
    .replace(/please make it/g, '')
    .replace(/make it/g, '')
    .replace(/change it to/g, '')
    .replace(/set it to/g, '')
    .replace(/how about/g, '')
    .replace(/what about/g, '')
    .replace(/let's try/g, '')
    .replace(/let's make it/g, '')
    .replace(/i want/g, '')
    .replace(/i'd like/g, '')
    .replace(/\?/g, '');

  // Descriptive commands
  if (normalizedContent.includes('pop') || normalizedContent.includes('stand out')) {
    changes.background = { color: '#FCD34D' };
    // Note: shadow and border not supported directly in CanvasLayout
  } else if (normalizedContent.includes('subtle') || normalizedContent.includes('tone it down')) {
    changes.background = { color: '#F9FAFB' };
  } else if (normalizedContent.includes('clean') || normalizedContent.includes('tidy')) {
    changes.background = { color: '#FFFFFF' };
    changes.grid = { enabled: false };
  } else if (normalizedContent.includes('busy') || normalizedContent.includes('complex')) {
    changes.grid = { enabled: true, size: 10 };
    changes.background = { type: 'pattern', pattern: { type: 'grid' } };
  }

  // Comparison commands
  if (normalizedContent.includes('like twitter') || normalizedContent.includes('twitter style')) {
    changes.background = { color: '#FFFFFF' };
    changes.theme = { colorScheme: 'light' };
  } else if (normalizedContent.includes('like google') || normalizedContent.includes('google style')) {
    changes.background = { color: '#FFFFFF' };
    changes.theme = { colorScheme: 'light' };
    changes.layout = { padding: { top: 20, right: 20, bottom: 20, left: 20 } };
  } else if (normalizedContent.includes('like apple') || normalizedContent.includes('apple style')) {
    changes.background = { color: '#FBFBFD' };
    changes.theme = { colorScheme: 'light' };
  }

  // Accessibility commands
  if (normalizedContent.includes('high contrast')) {
    changes.background = { color: '#000000' };
    changes.theme = { colorScheme: 'dark' };
  } else if (normalizedContent.includes('readable') || normalizedContent.includes('easy to read')) {
    changes.background = { color: '#FFFEF7' };
    changes.theme = { colorScheme: 'light' };
    changes.layout = { padding: { top: 30, right: 30, bottom: 30, left: 30 } };
  }

  // Handle relative color adjustments (darker, lighter, etc.)
  if (lowerContent.includes('darker') || lowerContent.includes('darken')) {
    changes.background = { color: adjustColorBrightness(currentBgColor, -30) };
  } else if (lowerContent.includes('lighter') || lowerContent.includes('lighten') || lowerContent.includes('brighter')) {
    changes.background = { color: adjustColorBrightness(currentBgColor, 30) };
  } else if (lowerContent.includes('much darker')) {
    changes.background = { color: adjustColorBrightness(currentBgColor, -50) };
  } else if (lowerContent.includes('much lighter') || lowerContent.includes('much brighter')) {
    changes.background = { color: adjustColorBrightness(currentBgColor, 50) };
  } else if (lowerContent.includes('slightly darker')) {
    changes.background = { color: adjustColorBrightness(currentBgColor, -15) };
  } else if (lowerContent.includes('slightly lighter') || lowerContent.includes('slightly brighter')) {
    changes.background = { color: adjustColorBrightness(currentBgColor, 15) };
  }
  // Background changes - more flexible parsing
  else if (lowerContent.includes('background') || lowerContent.includes('bg')) {
    // Look for color names or hex codes - expanded patterns
    const colorPatterns = [
      /#[a-fA-F0-9]{6}/,
      /#[a-fA-F0-9]{3}/,
      // Brand colors
      /\b(facebook|twitter|youtube|instagram|linkedin|whatsapp|spotify|netflix|amazon|google|microsoft|apple|slack)\s*(blue|red|green|purple|orange|gray|grey)?\b/i,
      // Extended variations
      /\b(very\s+light|very\s+dark|light|dark)?\s*(blue|red|green|yellow|purple|orange|pink|gray|grey)\b/i,
      // New color names
      /\b(navy|sky|crimson|rose|emerald|lime|mint|forest|amber|gold|cream|violet|indigo|lavender|coral|peach|slate|zinc|neutral|stone|silver|charcoal|cyan|magenta|teal|brown|tan|beige|ivory)\b/i,
      // Temperature colors
      /\b(warm|cool|cold|hot)\s*(gray|grey)?\b/i,
      // Basic colors
      /\b(white|black|transparent)\b/i
    ];

    for (const pattern of colorPatterns) {
      const colorMatch = content.match(pattern);
      if (colorMatch) {
        let color = colorMatch[0];
        // Extended color palette with variations
        const colorMap: Record<string, string> = {
          // Basic colors
          'blue': '#3B82F6',
          'light blue': '#60A5FA',
          'dark blue': '#1E40AF',
          'very light blue': '#DBEAFE',
          'very dark blue': '#1E3A8A',
          'navy': '#0F172A',
          'sky': '#0EA5E9',
          'sky blue': '#0EA5E9',

          'red': '#EF4444',
          'light red': '#FCA5A5',
          'dark red': '#991B1B',
          'very light red': '#FEE2E2',
          'very dark red': '#7F1D1D',
          'crimson': '#DC143C',
          'rose': '#F43F5E',
          'pink': '#EC4899',
          'hot pink': '#EC4899',

          'green': '#10B981',
          'light green': '#34D399',
          'dark green': '#047857',
          'very light green': '#D1FAE5',
          'very dark green': '#064E3B',
          'emerald': '#10B981',
          'lime': '#84CC16',
          'mint': '#A7F3D0',
          'forest': '#064E3B',

          'yellow': '#F59E0B',
          'light yellow': '#FCD34D',
          'dark yellow': '#B45309',
          'very light yellow': '#FEF3C7',
          'very dark yellow': '#78350F',
          'amber': '#F59E0B',
          'gold': '#FFD700',
          'cream': '#FFFDD0',

          'purple': '#8B5CF6',
          'light purple': '#A78BFA',
          'dark purple': '#6B21A8',
          'very light purple': '#EDE9FE',
          'very dark purple': '#581C87',
          'violet': '#8B5CF6',
          'indigo': '#6366F1',
          'lavender': '#E6E6FA',

          'orange': '#F97316',
          'light orange': '#FB923C',
          'dark orange': '#C2410C',
          'very light orange': '#FED7AA',
          'very dark orange': '#7C2D12',
          'coral': '#FF7F50',
          'peach': '#FFDAB9',

          'gray': '#6B7280',
          'grey': '#6B7280',
          'light gray': '#D1D5DB',
          'light grey': '#D1D5DB',
          'dark gray': '#374151',
          'dark grey': '#374151',
          'very light gray': '#F3F4F6',
          'very light grey': '#F3F4F6',
          'very dark gray': '#111827',
          'very dark grey': '#111827',
          'slate': '#475569',
          'zinc': '#71717A',
          'neutral': '#737373',
          'stone': '#78716C',
          'silver': '#C0C0C0',
          'charcoal': '#36454F',

          // Brand colors
          'facebook blue': '#1877F2',
          'twitter blue': '#1DA1F2',
          'youtube red': '#FF0000',
          'instagram purple': '#E4405F',
          'linkedin blue': '#0077B5',
          'whatsapp green': '#25D366',
          'spotify green': '#1DB954',
          'netflix red': '#E50914',
          'amazon orange': '#FF9900',
          'google blue': '#4285F4',
          'microsoft blue': '#0078D4',
          'apple gray': '#A8A8A8',
          'slack purple': '#4A154B',

          // Special colors
          'white': '#FFFFFF',
          'black': '#000000',
          'transparent': 'transparent',
          'cyan': '#06B6D4',
          'magenta': '#EC4899',
          'teal': '#14B8A6',
          'brown': '#92400E',
          'tan': '#D2B48C',
          'beige': '#F5F5DC',
          'ivory': '#FFFFF0',

          // Temperature colors
          'warm gray': '#78716C',
          'cool gray': '#6B7280',
          'warm': '#FB923C',
          'cool': '#06B6D4',
          'cold': '#0EA5E9',
          'hot': '#EF4444'
        };

        const normalizedColor = color.toLowerCase().replace(/\s+/g, ' ').trim();
        if (colorMap[normalizedColor]) {
          color = colorMap[normalizedColor];
        }

        changes.background = { color };
        break;
      }
    }

    // Gradient support with directions
    if (lowerContent.includes('gradient')) {
      let gradientType = 'linear';
      let direction = 'to bottom';

      if (lowerContent.includes('radial')) {
        gradientType = 'radial';
        direction = 'circle';
      } else if (lowerContent.includes('conic')) {
        gradientType = 'conic';
      }

      // Direction parsing for linear gradients
      if (lowerContent.includes('to right') || lowerContent.includes('horizontal')) {
        direction = 'to right';
      } else if (lowerContent.includes('to left')) {
        direction = 'to left';
      } else if (lowerContent.includes('to top') || lowerContent.includes('up')) {
        direction = 'to top';
      } else if (lowerContent.includes('to bottom') || lowerContent.includes('down') || lowerContent.includes('vertical')) {
        direction = 'to bottom';
      } else if (lowerContent.includes('diagonal')) {
        direction = 'to bottom right';
      }

      changes.background = {
        ...objectRecord(changes.background),
        type: 'gradient',
        gradientType,
        direction
      };
    }

    // Pattern support with variations
    if (lowerContent.includes('pattern') || lowerContent.includes('dot') || lowerContent.includes('stripe') || lowerContent.includes('checkerboard') || lowerContent.includes('grid pattern')) {
      let patternType = 'dots';

      if (lowerContent.includes('stripe')) {
        patternType = 'stripes';
      } else if (lowerContent.includes('checkerboard') || lowerContent.includes('chess')) {
        patternType = 'checkerboard';
      } else if (lowerContent.includes('grid')) {
        patternType = 'grid';
      } else if (lowerContent.includes('lines')) {
        patternType = 'lines';
      } else if (lowerContent.includes('waves')) {
        patternType = 'waves';
      }

      changes.background = {
        ...objectRecord(changes.background),
        type: 'pattern',
        pattern: { type: patternType }
      };
    }

    if (lowerContent.includes('transparent') || lowerContent.includes('clear') || lowerContent.includes('none')) {
      changes.background = { color: 'transparent' };
    }
  }

  // Also check for simple color commands without "background"
  if (!changes.background && (lowerContent.includes('blue') || lowerContent.includes('red') || lowerContent.includes('green'))) {
    const simpleColorMap: Record<string, string> = {
      'blue': '#3B82F6',
      'red': '#EF4444',
      'green': '#10B981',
      'yellow': '#F59E0B',
      'purple': '#8B5CF6',
      'orange': '#F97316',
      'pink': '#EC4899'
    };

    for (const [color, hex] of Object.entries(simpleColorMap)) {
      if (lowerContent.includes(color)) {
        changes.background = { color: hex };
        break;
      }
    }
  }

  // Grid changes
  if (lowerContent.includes('grid')) {
    if (lowerContent.includes('enable') || lowerContent.includes('show')) {
      changes.grid = { enabled: true };
    } else if (lowerContent.includes('disable') || lowerContent.includes('hide')) {
      changes.grid = { enabled: false };
    }
    const sizeMatch = content.match(/(\d+)\s*px/);
    if (sizeMatch) {
      changes.grid = { ...objectRecord(changes.grid), size: parseInt(sizeMatch[1], 10) };
    }
  }

  // Theme changes
  if (lowerContent.includes('dark mode') || lowerContent.includes('dark theme')) {
    changes.theme = { colorScheme: 'dark' };
  } else if (lowerContent.includes('light mode') || lowerContent.includes('light theme')) {
    changes.theme = { colorScheme: 'light' };
  }

  // Padding changes
  if (lowerContent.includes('padding')) {
    const paddingMatch = content.match(/(\d+)\s*px/);
    if (paddingMatch) {
      const padding = parseInt(paddingMatch[1], 10);
      changes.layout = {
        padding: { top: padding, right: padding, bottom: padding, left: padding }
      };
    } else if (lowerContent.includes('no padding') || lowerContent.includes('remove padding')) {
      changes.layout = {
        padding: { top: 0, right: 0, bottom: 0, left: 0 }
      };
    } else if (lowerContent.includes('more padding') || lowerContent.includes('increase padding')) {
      const currentPadding = canvasSettings?.layout?.padding?.top || 20;
      const newPadding = currentPadding + 10;
      changes.layout = {
        padding: { top: newPadding, right: newPadding, bottom: newPadding, left: newPadding }
      };
    } else if (lowerContent.includes('less padding') || lowerContent.includes('decrease padding')) {
      const currentPadding = canvasSettings?.layout?.padding?.top || 20;
      const newPadding = Math.max(0, currentPadding - 10);
      changes.layout = {
        padding: { top: newPadding, right: newPadding, bottom: newPadding, left: newPadding }
      };
    }
  }

  // Note: Margin is handled via padding since CanvasLayout doesn't have a margin property
  // Users asking for "margin" will get padding adjustments instead

  // Spacing/gap commands - Note: gap is not directly supported in CanvasLayout
  // Could be handled through padding adjustments instead

  // Opacity changes - apply to the current background color using rgba
  if (lowerContent.includes('opacity') || lowerContent.includes('transparent')) {
    const parsedBackground = objectRecord(changes.background);
    const currentColor = typeof parsedBackground.color === 'string'
      ? parsedBackground.color
      : canvasSettings?.background?.color || '#FFFFFF';
    let opacity = 1;

    const opacityMatch = content.match(/(\d+(?:\.\d+)?)\s*%?/);
    if (opacityMatch) {
      opacity = parseFloat(opacityMatch[1]);
      if (opacity > 1) opacity = opacity / 100; // Convert percentage to decimal
    } else if (lowerContent.includes('more transparent')) {
      opacity = 0.5; // Set to 50% transparency
    } else if (lowerContent.includes('less transparent') || lowerContent.includes('more opaque')) {
      opacity = 0.8; // Set to 80% opacity
    } else if (lowerContent.includes('fully transparent')) {
      opacity = 0;
    } else if (lowerContent.includes('fully opaque') || lowerContent.includes('no transparency')) {
      opacity = 1;
    } else if (lowerContent.includes('slightly transparent')) {
      opacity = 0.8;
    } else if (lowerContent.includes('very transparent')) {
      opacity = 0.2;
    }

    // Convert hex color to rgba with opacity
    if (currentColor && currentColor !== 'transparent') {
      const hex = currentColor.replace('#', '');
      let r: number, g: number, b: number;

      if (hex.length === 3) {
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
      } else if (hex.length === 6) {
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
      } else {
        // Default to white if unable to parse
        r = 255;
        g = 255;
        b = 255;
      }

      changes.background = { ...objectRecord(changes.background), color: `rgba(${r}, ${g}, ${b}, ${opacity})` };
    } else if (currentColor === 'transparent' || opacity === 0) {
      changes.background = { ...objectRecord(changes.background), color: 'transparent' };
    }
  }

  // Border changes - Note: border is not directly supported in CanvasLayout
  // These visual properties might need to be handled differently

  // Shadow changes - Note: shadow is not directly supported in CanvasLayout
  // These visual properties might need to be handled through theme settings

  // Zoom and scale commands
  if (lowerContent.includes('zoom')) {
    if (lowerContent.includes('zoom in') || lowerContent.includes('closer')) {
      changes.zoom = { action: 'in', amount: 0.2 };
    } else if (lowerContent.includes('zoom out') || lowerContent.includes('farther')) {
      changes.zoom = { action: 'out', amount: 0.2 };
    } else if (lowerContent.includes('fit') || lowerContent.includes('fit to screen')) {
      changes.zoom = { action: 'fit' };
    } else if (lowerContent.includes('actual size') || lowerContent.includes('100%') || lowerContent.includes('reset zoom')) {
      changes.zoom = { action: 'reset' };
    }
  }

  // Design presets and themes
  if (lowerContent.includes('minimal') || lowerContent.includes('minimalist')) {
    changes.background = { color: '#FFFFFF' };
    changes.theme = { colorScheme: 'light' };
    changes.layout = {
      padding: { top: 40, right: 40, bottom: 40, left: 40 }
    };
    changes.grid = { enabled: false };
  } else if (lowerContent.includes('modern')) {
    changes.background = { color: '#F9FAFB' };
    changes.theme = { colorScheme: 'light' };
    changes.layout = {
      padding: { top: 20, right: 20, bottom: 20, left: 20 }
    };
    changes.grid = { enabled: true, size: 20 };
  } else if (lowerContent.includes('corporate') || lowerContent.includes('professional')) {
    changes.background = { color: '#FFFFFF' };
    changes.theme = { colorScheme: 'light' };
    changes.layout = {
      padding: { top: 30, right: 30, bottom: 30, left: 30 }
    };
  } else if (lowerContent.includes('creative') || lowerContent.includes('playful')) {
    changes.background = {
      type: 'gradient',
      gradientType: 'linear',
      direction: 'to bottom right',
      color: '#FEF3C7'
    };
    changes.theme = { colorScheme: 'light' };
    changes.layout = {
      padding: { top: 25, right: 25, bottom: 25, left: 25 }
    };
  } else if (lowerContent.includes('glass') || lowerContent.includes('glassmorphism')) {
    changes.background = { color: 'rgba(255, 255, 255, 0.1)' };
    // Note: blur effect not directly supported in CanvasLayout
  } else if (lowerContent.includes('brutalist')) {
    changes.background = { color: '#FEF3C7' };
    changes.layout = {
      padding: { top: 0, right: 0, bottom: 0, left: 0 }
    };
  } else if (lowerContent.includes('neumorphism') || lowerContent.includes('soft ui')) {
    changes.background = { color: '#E5E7EB' };
    changes.layout = {
      padding: { top: 30, right: 30, bottom: 30, left: 30 }
    };
  }

  // Holiday/seasonal themes
  if (lowerContent.includes('christmas') || lowerContent.includes('holiday')) {
    changes.background = { color: '#DC2626' };
    changes.theme = { colorScheme: 'dark' };
  } else if (lowerContent.includes('halloween')) {
    changes.background = { color: '#EA580C' };
    changes.theme = { colorScheme: 'dark' };
  } else if (lowerContent.includes('summer')) {
    changes.background = { color: '#FCD34D' };
    changes.theme = { colorScheme: 'light' };
  } else if (lowerContent.includes('winter')) {
    changes.background = { color: '#DBEAFE' };
    changes.theme = { colorScheme: 'light' };
  }

  // Mood-based settings
  if (lowerContent.includes('energetic') || lowerContent.includes('vibrant')) {
    changes.background = { color: '#FCD34D' };
  } else if (lowerContent.includes('calm') || lowerContent.includes('peaceful')) {
    changes.background = { color: '#DBEAFE' };
    changes.layout = { padding: { top: 40, right: 40, bottom: 40, left: 40 } };
  } else if (lowerContent.includes('serious') || lowerContent.includes('formal')) {
    changes.background = { color: '#1F2937' };
    changes.theme = { colorScheme: 'dark' };
  }

  // Reset/clear commands
  if (lowerContent.includes('reset') || lowerContent.includes('default')) {
    if (lowerContent.includes('background')) {
      changes.background = { color: '#FFFFFF' };
    }
    if (lowerContent.includes('all') || lowerContent === 'reset' || lowerContent === 'default') {
      changes.background = { color: '#FFFFFF' };
      changes.grid = { enabled: false };
      changes.theme = { colorScheme: 'light' };
      changes.layout = {
        padding: { top: 20, right: 20, bottom: 20, left: 20 }
      };
    }
  }

  return Object.keys(changes).length > 0 ? changes : undefined;
};

  return {
    createMultipleComponents,
    createComponentFromCommand,
    checkAndCreateTemplate,
    checkAndCreateComponent,
    parseCanvasChanges,
  };
}
